import React, { useState } from 'react';
import { Alert, Box, Stack, TextField, Typography } from '@mui/material';

// COMPONENTS
import LoaderButton from '../../components/common/LoaderButton';
import FullscreenLayout from '../../components/layouts/FullscreenLayout';
import BackButton from '../../components/common/BackButton';

// I18N
import PasswordRecoveryFormattedMessages from '../../messages/PasswordRecoveryFormattedMessages';

// IMGS
import CompleteSMSLogo from '../../images/logo-CompleteSMS.svg';

// NETWORK
import useRecoverPasswordRequest from '../../requests/RecoverPasswordRequest';
import { validateEmail } from '../../utils/validate';

const PasswordRecovery = ({ navigate, errorHandler }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [hasRegistered, setHasRegistered] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorText, setErrorText] = useState('');

  const { recoverPassword } = useRecoverPasswordRequest();

  /*
    Validates form
  */
  const isFormValid = () => {
    return email.length > 0;
  };

  /*
    Handles setting state for form inputs
  */
  const handleChange = (event) => {
    setEmail(event.target.value);
  };

  /*
    Sets error text if in an error state, else returns nulls
  */
  const setError = () => {
    if (!hasError) return null;
    return <Alert severity="error">{errorText}</Alert>;
  };

  /**
   * Goes back to the login page.
   */
  const backToLogin = () => {
    navigate('/');
  };

  /*
    Handles form submission
  */
  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setHasError(false);

    try {
      if (!validateEmail(email)) {
        return (
          setIsLoading(false),
          setHasError(true),
          setErrorText('The email provided is not in a valid format.')
        );
      }

      // Make request to start Customer password recovery
      let error = await recoverPassword(email, errorHandler);
      // If no error, go to next state
      if (!error) {
        return setIsLoading(false), setHasRegistered(true), setHasError(false);
      }

      return setIsLoading(false), setHasError(true), setErrorText(error.error);
    } catch (e) {
      console.log('Network error:', e);
      return (
        setIsLoading(false), setHasError(true), setErrorText('Network Error')
      );
    }
  };

  /*
    Render the Register Form components based on state
  */
  const renderForm = () => {
    // User has completed the Registration process
    if (hasRegistered) {
      return (
        <>
          <Typography variant="h4" gutterBottom textAlign="center">
            {PasswordRecoveryFormattedMessages.successText}
          </Typography>

          <Typography textAlign="center">
            {PasswordRecoveryFormattedMessages.emailSent} {email}
          </Typography>
        </>
      );
    }
    // User has not completed the Registration process
    else {
      return (
        <Box component="form" onSubmit={handleSubmit}>
          <Stack gap={2}>
            <TextField
              id="email"
              name="email"
              label={PasswordRecoveryFormattedMessages.recoverPasswordTitle}
              variant="outlined"
              size="small"
              fullWidth
              type="email"
              value={email}
              onChange={handleChange}
              autoFocus
            />

            {setError()}

            <LoaderButton
              fullWidth
              disabled={!isFormValid()}
              type="submit"
              isLoading={isLoading}
              text={PasswordRecoveryFormattedMessages.recoveryButtonText}
              loadingText={PasswordRecoveryFormattedMessages.loading}
            />
          </Stack>
        </Box>
      );
    }
  };

  return (
    <FullscreenLayout fullHeight>
      <Stack
        sx={{
          justifyContent: 'center',
          alignItems: 'center',
          flex: 1,
          gap: 3,
        }}
      >
        <Box
          sx={{
            backgroundColor: 'common.white',
            borderRadius: 4,
            paddingTop: 2,
            paddingBottom: 4,
            paddingX: { xs: 2, md: 4 },
            width: 400,
            maxWidth: '100%',
          }}
        >
          <BackButton
            onClick={backToLogin}
            backButtonText={PasswordRecoveryFormattedMessages.backToLogin}
          />

          <Box
            sx={{
              marginBottom: 4,
              marginTop: 2,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <img
              src={CompleteSMSLogo}
              alt={`CompleteSMS Logo ${process.env.REACT_APP_COLOUR}`}
              width={200}
            />
          </Box>

          {renderForm()}
        </Box>
      </Stack>
    </FullscreenLayout>
  );
};

export default PasswordRecovery;
